import React, { useState } from 'react';
import { Item, WebsiteResultErrorCategory } from '../interfaces';
import InfoPopup from './InfoPopup';
import SettingsPopup from './SettingsPopup';

const ERROR_CATEGORY_LABELS: Record<WebsiteResultErrorCategory, string> = {
  timeout: 'Timed out',
  bot_blocked: 'Blocked',
  no_products_found: 'No products found',
  ai_error: 'AI error',
  tab_creation_failed: "Couldn't load",
  no_content: 'No usable data',
};

interface ItemRowProps {
  item: Item;
  onUpdate: (item: Item, newName: string) => Promise<boolean>;
  onDelete: (item: Item) => void;
  onRefresh?: (item: Item) => void;
  columns: string[];
  onSaveSettings: (item: Item, settings: { is_tracked_periodically: boolean; selected_websites: string[]; use_ai_to_find_product: boolean }) => void;
  isGeminiProcessing: boolean;
  userId: string | null;
}

const ItemRow: React.FC<ItemRowProps> = ({ item, onUpdate, onDelete, onRefresh, columns, onSaveSettings, isGeminiProcessing, userId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(item.item_name);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const handleUpdate = async () => {
    if (name !== item.item_name) {
      const updateCompleted = await onUpdate(item, name);
      if (updateCompleted) {
        setIsEditing(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleSaveSettings = (item: Item, settings: { is_tracked_periodically: boolean; selected_websites: string[]; use_ai_to_find_product: boolean }) => {
    onSaveSettings(item, settings);
  };

  return (
    <>
      <tr className={isExpanded ? 'bg-light bg-opacity-25' : ''}>
        <td className="ps-4 item-name-cell-bg">
          {isEditing ? (
            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUpdate()}
                disabled={isGeminiProcessing}
                autoFocus
              />
              <button className="btn btn-sm btn-primary" onClick={handleUpdate}>Save</button>
              <button className="btn btn-sm btn-light" onClick={() => setIsEditing(false)}>Cancel</button>
            </div>
          ) : (
            <div className="d-flex align-items-center">
              <span className="fw-bold fs-5 me-3 text-dark">{item.item_name}</span>
              <div className="action-buttons d-flex gap-2">
                <button className="btn btn-link btn-sm p-0 text-muted hover-primary" onClick={() => setIsEditing(true)} title="Edit name">
                  <i className="bi bi-pencil"></i>
                </button>
                <button className="btn btn-link btn-sm p-0 text-muted hover-primary" onClick={() => setShowSettings(true)} title="Settings">
                  <i className="bi bi-gear"></i>
                </button>
              </div>
            </div>
          )}
        </td>

        {columns.includes('Last Updated') && (
          <td className="text-nowrap">
            <span className="d-md-none small text-muted text-uppercase fw-bold" style={{ minWidth: '100px', display: 'inline-block' }}>Updated:</span>
            {item.last_updated && item.last_updated !== new Date(0).toISOString() ? (
              <span className="small text-muted">
                {new Date(item.last_updated).toLocaleDateString()}
              </span>
            ) : (
              <span className="badge bg-light text-muted fw-normal">Pending</span>
            )}
          </td>
        )}

        {columns.includes('Price/Link') && (
          <td className="text-nowrap">
            <span className="d-md-none small text-muted text-uppercase fw-bold" style={{ minWidth: '100px', display: 'inline-block' }}>Best Price:</span>
            {item.link && item.price ? (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary fw-bold"
                onClick={() => {
                  if (window.gtag && userId) {
                    window.gtag('event', 'product_link_clicked', { item_id: item.id, item_name: item.item_name, user_id: userId, link_url: item.link });
                  }
                }}>
                ${item.price} <i className="bi bi-box-arrow-up-right ms-1 small"></i>
              </a>
            ) : item.error ? (
              <span className="badge bg-danger bg-opacity-10 text-danger fw-normal">Failed</span>
            ) : (item.use_ai_to_find_product !== false) ? (
              <div className="d-flex align-items-center text-muted small">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Searching...
              </div>
            ) : (
              <span className="text-muted small">-</span>
            )}
          </td>
        )}

        {columns.includes('Actions') && (
          <td className="text-end pe-4">
            <div className="d-flex align-items-center gap-3 w-100">
              <span className="d-md-none small text-muted text-uppercase fw-bold" style={{ minWidth: '100px', display: 'inline-block' }}>Actions:</span>
              <div className="d-flex align-items-center gap-3 ms-md-auto">
                {(item.reason || item.error) && (
                  <button 
                    className="btn btn-link btn-sm p-0 text-muted"
                    onClick={() => {
                      setShowInfo(true);
                      if (window.gtag && userId) {
                        window.gtag('event', 'reason_viewed', { item_id: item.id, item_name: item.item_name, user_id: userId, reason_text: (item.reason || item.error || '').substring(0, 100) });
                      }
                    }}
                    title="View Info"
                  >
                    <i className={item.additional_info_needed ? 'bi bi-exclamation-triangle text-warning' : item.reason ? 'bi bi-info-circle' : 'bi bi-exclamation-triangle text-danger'}></i>
                  </button>
                )}
                
                {(item.website_results || item.error) && (
                  <button className="btn btn-light btn-sm rounded-circle shadow-sm" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setIsExpanded(!isExpanded)} title="Toggle Details">
                    <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                  </button>
                )}

                {onRefresh && item.use_ai_to_find_product !== false && (
                  <button
                    className="btn btn-link btn-sm p-0 text-muted hover-primary"
                    onClick={() => {
                      onRefresh(item);
                      if (window.gtag && userId) {
                        window.gtag('event', 'item_refresh_clicked', { item_id: item.id, item_name: item.item_name, user_id: userId });
                      }
                    }}
                    title="Refresh price"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                )}

                <button className="btn btn-link btn-sm p-0 text-muted hover-danger" onClick={() => {
                  if (window.confirm('Are you sure you want to delete this item?')) {
                    onDelete(item);
                  }
                }} title="Delete item">
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </td>
        )}
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={columns.length} className="bg-light bg-opacity-10 p-0">
            <div className="p-4 border-top">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="h6 mb-0 fw-bold">Detailed Results</h5>
                {item.website_results && item.last_updated && (
                  <span className="small text-muted">Updated: {new Date(item.last_updated).toLocaleString()}</span>
                )}
              </div>
              <div className="row g-3">
              {
                item.website_results && (() => {
                  const successfulResults = item.website_results.filter(result => !result.error && result.best_option);
                  const failedResults = item.website_results.filter(result => result.error);
                  const noResults = item.website_results.filter(result => !result.error && !result.best_option);

                  return (
                    <>
                      {successfulResults.map(result => (
                        <div key={result.website_name} className="col-md-6 col-lg-4">
                          <div className="card h-100 border shadow-none bg-white">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-bold">{result.website_name}</span>
                                <span className="badge bg-success bg-opacity-10 text-success fw-bold">
                                  ${result.best_option?.price}
                                </span>
                              </div>
                              <p className="small text-muted mb-2">{result.best_option_reason}</p>
                              <a href={result.best_option?.link} target="_blank" rel="noopener noreferrer"
                                className="btn btn-sm btn-link p-0 text-primary fw-medium text-decoration-none"
                                onClick={() => {
                                  if (window.gtag && userId && result.best_option) {
                                    window.gtag('event', 'product_link_clicked', { item_id: item.id, item_name: item.item_name, user_id: userId, link_url: result.best_option.link });
                                  }
                                }}>
                                View on {result.website_name} <i className="bi bi-arrow-right ms-1"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {(failedResults.length > 0 || noResults.length > 0) && (
                        <div className="col-12 mt-3">
                          <div className="bg-white rounded-3 p-3 border">
                            <h6 className="small fw-bold text-muted text-uppercase mb-2">Other Sources</h6>
                            <div className="d-flex flex-wrap gap-2">
                              {failedResults.map(result => (
                                <span key={result.website_name} className="badge bg-danger bg-opacity-10 text-danger fw-normal px-2 py-1" title={result.error}>
                                  {result.website_name}: {(result.error_category && ERROR_CATEGORY_LABELS[result.error_category]) || 'Failed'}
                                </span>
                              ))}
                              {noResults.map(result => (
                                <span key={result.website_name} className="badge bg-warning bg-opacity-10 text-warning fw-normal px-2 py-1" title="No result and no error recorded for this site">
                                  {result.website_name}: No data
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()
              }
              </div>
            </div>
          </td>
        </tr>
      )}
      {showInfo && (item.reason || item.error) && <InfoPopup reason={(item.reason || item.error)!} onClose={() => setShowInfo(false)} />}
      {showSettings && (
        <SettingsPopup
          item={item}
          onClose={() => setShowSettings(false)}
          onSave={handleSaveSettings}
        />
      )}
    </>
  );
};

export default ItemRow;